/**
 * The Azure picker's identity actions: switch az CLI ↔ Entra, Refresh, sign in
 * (also with a different account) and sign out. A failed sign-in (MFA, consent,
 * cancelled…) is kept as `signInError` and shown instead of pretending the
 * switch worked; the preference store remembers an explicit choice. Analytics
 * fire here (`feature:azure_browse_identity`, a sign-in's `azure_browse_error`).
 */
import { useState } from 'react';
import type { useAnalytics } from '@/core/analytics/useAnalytics';
import type { AzureDiscoveryError, AzureIdentitySource } from '../../../types/azureDiscovery';
import { useAzurePickerStore } from './azurePickerStore';

interface Deps {
  activeSource: AzureIdentitySource | undefined;
  setActiveSource: (source: AzureIdentitySource | undefined) => void;
  resolvedSource: AzureIdentitySource | undefined;
  setInteractive: (value: boolean) => void;
  refetch: () => Promise<void>;
  track: ReturnType<typeof useAnalytics>['track'];
}

export function useAzurePickerIdentityActions(deps: Deps) {
  const { activeSource, setActiveSource, resolvedSource, setInteractive, refetch, track } = deps;
  const setPreferredIdentity = useAzurePickerStore((s) => s.setPreferredIdentity);
  const [signInError, setSignInError] = useState<AzureDiscoveryError | undefined>(undefined);
  const effectiveSource = activeSource ?? resolvedSource;

  const switchTo = (source: AzureIdentitySource) => {
    if (source === effectiveSource) return;
    track('feature:azure_browse_identity', { source });
    setInteractive(false);
    setPreferredIdentity(source);
    setActiveSource(source);
  };

  const refresh = () => {
    setInteractive(false);
    void refetch();
  };

  const signInEntra = async (fromNoIdentity: boolean) => {
    setInteractive(true);
    const result = await window.electronAPI?.azureDiscovery.identity({ action: 'sign-in-different', preferred: 'entra' });
    if (result && !result.ok) {
      track('feature:azure_browse_error', { code: result.error.code });
      setSignInError(result.error);
      return;
    }
    setSignInError(undefined);
    if (fromNoIdentity && effectiveSource !== 'entra') track('feature:azure_browse_identity', { source: 'entra' });
    setPreferredIdentity('entra');
    if (effectiveSource === 'entra') void refetch();
    else setActiveSource('entra');
  };

  const signOut = async () => {
    await window.electronAPI?.azureDiscovery.identity({ action: 'sign-out' });
    setInteractive(false);
    setPreferredIdentity(undefined);
    setSignInError(undefined);
    if (activeSource === undefined) void refetch();
    else setActiveSource(undefined);
  };

  return { effectiveSource, signInError, switchTo, refresh, signInEntra, signOut };
}
